import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import isFunction from 'lodash/isFunction';
import { flushSync } from 'react-dom';

import { PreparedApiErrorsType } from 'src/models/BaseApi';
import { SafeAnyType } from 'src/utils/safeAny';
import { BaseStoreEntityType } from 'src/store/types';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';
import { UseAnyRequestConfigType } from 'src/models/useAnyRequest';

type ConfigType<Response, Error, Data> = UseAnyRequestConfigType<Response, Error, Data> & {
  initialState?: Response | null;
};

/**
 * @deprecated use useRequest{@see useRequest} or useOneRequest{@see useOneRequest}
 * helper to save response data from useAnyRequest hook {@see useAnyRequest}
 *
 * @param uniqueId - prop of useAnyRequest hook {@see useAnyRequest}
 * @param onSuccess - prop of useAnyRequest hook (optional) {@see useAnyRequest}
 * @param initialState - response initial state (optional)
 * @param config - rest props of useAnyRequest hook (optional) {@see useAnyRequest}
 */
export const useAnyRequestState = <Response = SafeAnyType, Error = PreparedApiErrorsType, Data = SafeAnyType>(
  uniqueId: string | null,
  { onSuccess, initialState = null, ...config }: ConfigType<Response, Error, Data> = {}
): [Response | null, BaseStoreEntityType<Response, Error>, Dispatch<SetStateAction<Response | null>>] => {
  const [state, setState] = useState<Response | null>(initialState);

  const onSuccessLocal = useCallback(
    (response: Response) => {
      flushSync(() => {
        setState(response);
      });

      if (isFunction(onSuccess)) {
        onSuccess(response);
      }
    },
    [onSuccess, setState]
  );

  const response = useAnyRequest<Response, Error, Data>(uniqueId, { ...config, onSuccess: onSuccessLocal });

  return [response.response || state, response, setState];
};
